/**
 * Main App Router — defines all routes with role-based guards.
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import {
  ProtectedRoute,
  PatientRoute,
  DoctorRoute,
  AdminRoute,
  PublicOnlyRoute,
} from './components/RouteGuards';
import InstallPrompt from './components/InstallPrompt';

// Public pages
import LandingPage from './pages/LandingPage';
import Login from './pages/auth/Login';
import PatientRegister from './pages/auth/PatientRegister';
import DoctorRegister from './pages/auth/DoctorRegister';
import DoctorPending from './pages/auth/DoctorPending';

// Patient pages
import PatientDashboard from './pages/patient/Dashboard';
import PatientProfile from './pages/patient/Profile';
import DoctorSearch from './pages/patient/DoctorSearch';
import PatientAppointments from './pages/patient/Appointments';
import BookAppointment from './pages/patient/BookAppointment';
import EmergencyConsultation from './pages/patient/Emergency';
import PatientDoctorProfile from './pages/patient/DoctorProfileView';
import PatientPrescriptions from './pages/patient/Prescriptions';
import PatientPrescriptionDetail from './pages/patient/PrescriptionDetail';
import PatientNotifications from './pages/patient/Notifications';
import PaymentPage from './pages/patient/Payment';

// Doctor pages
import DoctorDashboard from './pages/doctor/Dashboard';
import DoctorProfile from './pages/doctor/Profile';
import DoctorQueue from './pages/doctor/Queue';
import DoctorSchedule from './pages/doctor/Schedule';
import DoctorConsultations from './pages/doctor/Consultations';
import DoctorPatientProfile from './pages/doctor/PatientProfileView';
import DoctorPrescriptions from './pages/doctor/Prescriptions';
import WritePrescription from './pages/doctor/WritePrescription';
import AcceptEmergency from './pages/doctor/AcceptEmergency';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import VerifyDoctors from './pages/admin/VerifyDoctors';
import UserManagement from './pages/admin/UserManagement';
import SystemSettings from './pages/admin/Settings';
import AdminPayments from './pages/admin/Payments';

// Shared pages
import VideoConsultation from './pages/consultation/VideoConsultation';

function App() {
  return (
    <Router>
      <AuthProvider>
        <NotificationsProvider>
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              borderRadius: '12px',
              padding: '12px 16px',
              fontSize: '14px',
            },
          }}
        />
        <InstallPrompt />

        <Routes>
          {/* ========== PUBLIC ROUTES ========== */}
          <Route
            path="/"
            element={
              <PublicOnlyRoute>
                <LandingPage />
              </PublicOnlyRoute>
            }
          />

          {/* Auth: Login */}
          <Route
            path="/patient/login"
            element={
              <PublicOnlyRoute role="patient">
                <Login role="patient" />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/doctor/login"
            element={
              <PublicOnlyRoute role="doctor">
                <Login role="doctor" />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/admin/login"
            element={
              <PublicOnlyRoute role="admin">
                <Login role="admin" />
              </PublicOnlyRoute>
            }
          />

          {/* Auth: Register */}
          <Route
            path="/patient/register"
            element={
              <PublicOnlyRoute role="patient">
                <PatientRegister />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/doctor/register"
            element={
              <PublicOnlyRoute role="doctor">
                <DoctorRegister />
              </PublicOnlyRoute>
            }
          />

          {/* Doctor pending verification */}
          <Route path="/doctor/pending" element={<DoctorPending />} />

          {/* ========== PATIENT ROUTES ========== */}
          <Route
            path="/patient/dashboard"
            element={
              <PatientRoute>
                <PatientDashboard />
              </PatientRoute>
            }
          />
          <Route
            path="/patient/profile"
            element={
              <PatientRoute>
                <PatientProfile />
              </PatientRoute>
            }
          />
          <Route
            path="/patient/search-doctors"
            element={
              <PatientRoute>
                <DoctorSearch />
              </PatientRoute>
            }
          />
          <Route
            path="/patient/appointments"
            element={
              <PatientRoute>
                <PatientAppointments />
              </PatientRoute>
            }
          />
          <Route
            path="/patient/appointments/book"
            element={
              <PatientRoute>
                <BookAppointment />
              </PatientRoute>
            }
          />
          <Route
            path="/patient/appointments/:id"
            element={
              <PatientRoute>
                <PatientAppointments />
              </PatientRoute>
            }
          />
          <Route
            path="/patient/emergency"
            element={
              <PatientRoute>
                <EmergencyConsultation />
              </PatientRoute>
            }
          />
          <Route
            path="/patient/doctors/:uid"
            element={
              <PatientRoute>
                <PatientDoctorProfile />
              </PatientRoute>
            }
          />
          <Route
            path="/patient/prescriptions"
            element={
              <PatientRoute>
                <PatientPrescriptions />
              </PatientRoute>
            }
          />
          <Route
            path="/patient/prescriptions/:id"
            element={
              <PatientRoute>
                <PatientPrescriptionDetail />
              </PatientRoute>
            }
          />
          <Route
            path="/patient/notifications"
            element={
              <PatientRoute>
                <PatientNotifications />
              </PatientRoute>
            }
          />
          <Route
            path="/patient/payment"
            element={
              <PatientRoute>
                <PaymentPage />
              </PatientRoute>
            }
          />

          {/* ========== DOCTOR ROUTES ========== */}
          <Route
            path="/doctor/dashboard"
            element={
              <DoctorRoute>
                <DoctorDashboard />
              </DoctorRoute>
            }
          />
          <Route
            path="/doctor/profile"
            element={
              <DoctorRoute>
                <DoctorProfile />
              </DoctorRoute>
            }
          />
          <Route
            path="/doctor/queue"
            element={
              <DoctorRoute>
                <DoctorQueue />
              </DoctorRoute>
            }
          />
          <Route
            path="/doctor/patients/:uid"
            element={
              <DoctorRoute>
                <DoctorPatientProfile />
              </DoctorRoute>
            }
          />
          <Route
            path="/doctor/schedule"
            element={
              <DoctorRoute>
                <DoctorSchedule />
              </DoctorRoute>
            }
          />
          <Route
            path="/doctor/consultations"
            element={
              <DoctorRoute>
                <DoctorConsultations />
              </DoctorRoute>
            }
          />
          <Route
            path="/doctor/prescriptions"
            element={
              <DoctorRoute>
                <DoctorPrescriptions />
              </DoctorRoute>
            }
          />
          <Route
            path="/doctor/prescriptions/write"
            element={
              <DoctorRoute>
                <WritePrescription />
              </DoctorRoute>
            }
          />
          <Route
            path="/doctor/consultation/start"
            element={
              <DoctorRoute>
                <VideoConsultation />
              </DoctorRoute>
            }
          />
          <Route
            path="/doctor/accept-emergency"
            element={
              <DoctorRoute>
                <AcceptEmergency />
              </DoctorRoute>
            }
          />

          {/* ========== ADMIN ROUTES ========== */}
          <Route
            path="/admin/dashboard"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/verify-doctors"
            element={
              <AdminRoute>
                <VerifyDoctors />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <UserManagement />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <AdminRoute>
                <SystemSettings />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/payments"
            element={
              <AdminRoute>
                <AdminPayments />
              </AdminRoute>
            }
          />

          {/* ========== SHARED ROUTES ========== */}
          <Route
            path="/consultation"
            element={
              <ProtectedRoute>
                <VideoConsultation />
              </ProtectedRoute>
            }
          />

          {/* ========== FALLBACK ========== */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </NotificationsProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
